/****************
 * ONE WAY TO WRITE A CONTRACT'S NAME. 2026-10-01.
 *
 * Guillaume, on the trade page: "asset have USDT twice, it's like
 * ASSETUSDT/USDT, and it does this everywhere." The venue symbol already ends
 * in its quote currency ("BTCUSDT"), and screens were adding "/USDT" to it, or
 * removing the text "USDT" wherever it stood, each in its own way.
 *
 * Everything that shows a pair or a base coin goes through here. Tables keep
 * showing the venue's own symbol ("BTCUSDT"), which is what the exchange shows.
 ****************/

// Longest first: "USDT" must not be read as base "USD" + "T", and FDUSD ends in "USD".
const QUOTES = ['FDUSD', 'USDT', 'USDC', 'BUSD', 'TUSD', 'USDP', 'USDS', 'DAI', 'USD'];

export interface SymbolParts { base: string; quote: string }

/**
 * "BTCUSDT" -> BTC + USDT. Also "BTC/USDT", "BTC-USDT", "BTC_USDT" (MEXC),
 * "u1000PEPEUSDT" and "1000PEPEUSDT" (the multiplier stays with the base, it is
 * part of the contract), "CRCLSTOCKUSDT" (MEXC stocks), "BTCUSDC", and a bare
 * "BTC" (no quote). A stablecoin on its own ("USDT") is a base with no quote:
 * the quote is only removed when something is left.
 */
export function splitSymbol(symbol: string | null | undefined): SymbolParts {
  const raw = String(symbol ?? '').trim();
  if (!raw) return { base: '', quote: '' };
  const sep = raw.match(/^(.+?)[/_-]([A-Za-z]+)(?:[:_-].*)?$/);
  if (sep && QUOTES.includes(sep[2].toUpperCase())) {
    return { base: sep[1], quote: sep[2].toUpperCase() };
  }
  const upper = raw.toUpperCase();
  if (QUOTES.includes(upper)) return { base: raw, quote: '' };
  for (const q of QUOTES) {
    if (upper.endsWith(q) && raw.length > q.length) {
      return { base: raw.slice(0, -q.length), quote: q };
    }
  }
  return { base: raw, quote: '' };
}

/** The coin without its quote: "BTCUSDT" -> "BTC". */
export function baseCoin(symbol: string | null | undefined): string {
  return splitSymbol(symbol).base;
}

/** The pair, quote written once: "BTCUSDT" -> "BTC/USDT", "BTC/USDT" -> "BTC/USDT", "BTC" -> "BTC". */
export function pairLabel(symbol: string | null | undefined): string {
  const { base, quote } = splitSymbol(symbol);
  return quote ? `${base}/${quote}` : base;
}
